<template>
  <v-dialog :value="open" max-width="980" persistent scrollable @input="$emit('close')">
    <v-card class="vi-card">

      <v-card-title class="vi-title">
        <span class="material-symbols-outlined">move_group</span>
        Importer des graphiques d'un autre univers
        <v-spacer></v-spacer>
        <span class="vi-step">Étape {{ step }} / 3</span>
        <v-btn icon small class="ml-2" @click="$emit('close')">
          <v-icon small>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="vi-body">

        <!-- Étape 1 : univers source -->
        <template v-if="step === 1">
          <p class="vi-intro">
            Les graphiques choisis sont dupliqués dans « {{ site.name }} ».
            L'univers source n'est pas modifié.
          </p>

          <div v-if="!candidates.length" class="vi-empty">
            Aucun autre univers disponible chez ce client.
          </div>

          <v-select
              v-else
              v-model="sourceId"
              :items="candidates"
              item-text="name"
              item-value="id"
              label="Univers source"
              outlined dense hide-details
              prepend-inner-icon="domain"
              @change="onSourceChange"
          ></v-select>

          <div v-if="loadingViz" class="vi-loading">Chargement des graphiques…</div>

          <div v-else-if="sourceId && !sourceVisualizations.length" class="vi-empty">
            Cet univers n'a aucun graphique.
          </div>
        </template>

        <!-- Étape 2 : graphiques -->
        <template v-else-if="step === 2">
          <p class="vi-intro">
            Sélectionnez les graphiques à dupliquer.
          </p>

          <div class="vi-list">
            <label
                v-for="viz in sourceVisualizations"
                :key="viz.id"
                class="vi-item"
                :class="{ selected: pickedIds.includes(viz.id) }"
            >
              <input
                  type="checkbox"
                  :checked="pickedIds.includes(viz.id)"
                  @change="togglePick(viz)"
              />
              <span class="vi-item-body">
                <span class="vi-item-name">{{ viz.name }}</span>
                <span class="vi-item-meta">
                  {{ axeCount(viz) }} axe(s) · {{ mesureCount(viz) }} mesure(s)
                  <template v-if="viz.component"> · {{ viz.component }}</template>
                </span>
              </span>
            </label>
          </div>
        </template>

        <!-- Étape 3 : correspondance des mesures -->
        <template v-else>
          <p class="vi-intro">
            Les identifiants de mesure diffèrent d'un univers à l'autre. Indiquez
            à gauche la mesure d'origine, à droite celle de « {{ site.name }} »
            qui la remplace. Une ligne laissée vide sera retirée du graphique.
          </p>

          <div v-if="!sourceMesures.length" class="vi-empty">
            Les graphiques sélectionnés n'utilisent aucune mesure.
          </div>

          <template v-else>
            <div class="vi-map-head">
              <span>Mesure importée</span>
              <span></span>
              <span>Mesure de cet univers</span>
            </div>

            <div
                v-for="m in sourceMesures"
                :key="m.key"
                class="vi-map-row"
                :class="{ unmapped: !mapping[m.key] }"
            >
              <div class="vi-map-source">
                <span class="vi-map-name">{{ m.name }}</span>
                <span class="vi-map-col">{{ m.csv_column }}</span>
              </div>

              <span class="vi-map-arrow">→</span>

              <div class="vi-map-target">
                <v-select
                    v-model="mapping[m.key]"
                    :items="targetItems"
                    item-text="label"
                    item-value="id"
                    placeholder="Ne pas importer"
                    outlined dense hide-details clearable
                ></v-select>
              </div>
            </div>

            <v-alert
                v-if="unmappedCount"
                dense text type="warning"
                class="vi-warn"
            >
              {{ unmappedCount }} mesure(s) sans correspondance seront retirées
              des graphiques dupliqués. Un axe qui se retrouve vide est supprimé.
            </v-alert>

            <v-alert v-if="autoMatched" dense text type="info" class="vi-warn">
              {{ autoMatched }} correspondance(s) proposée(s) automatiquement sur
              la colonne source. Vérifiez-les avant de valider.
            </v-alert>
          </template>
        </template>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="vi-actions">
        <v-btn v-if="step > 1" text :disabled="working" @click="step--">
          <v-icon left small>arrow_back</v-icon>
          Retour
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text :disabled="working" @click="$emit('close')">Annuler</v-btn>

        <v-btn
            v-if="step === 1"
            color="#2d5a3d" dark
            :disabled="!sourceVisualizations.length"
            @click="step = 2"
        >Continuer</v-btn>

        <v-btn
            v-else-if="step === 2"
            color="#2d5a3d" dark
            :disabled="!pickedIds.length"
            @click="goToMapping"
        >Continuer</v-btn>

        <v-btn
            v-else
            color="#2d5a3d" dark
            :loading="working"
            @click="confirm"
        >
          <v-icon left small>content_copy</v-icon>
          Dupliquer {{ pickedIds.length }} graphique(s)
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "VisualizationImportDialog",

  props: {
    open: {type: Boolean, default: false},
    // L'univers de destination
    site: {type: Object, required: true},
    clientSites: {type: Array, default: () => []},
  },

  data() {
    return {
      step: 1,
      sourceId: null,
      sourceVisualizations: [],
      pickedIds: [],
      // clé de mesure source -> id de capteur cible
      mapping: {},
      autoMatched: 0,
      loadingViz: false,
      working: false,
    };
  },

  computed: {
    candidates() {
      return this.clientSites.filter((s) => s.id !== this.site.id);
    },

    targetSensors() {
      return this.site.sensors || [];
    },

    targetItems() {
      return this.targetSensors.map((s) => ({
        id: s.id,
        label: s.csv_column && s.csv_column !== s.name
            ? `${s.name} (${s.csv_column})`
            : s.name,
      }));
    },

    pickedVisualizations() {
      return this.sourceVisualizations.filter((v) => this.pickedIds.includes(v.id));
    },

    /** Mesures distinctes utilisées par les graphiques sélectionnés. */
    sourceMesures() {
      const seen = {};
      const out = [];

      for (const viz of this.pickedVisualizations) {
        for (const axe of this.axesOf(viz)) {
          for (const mesure of axe.mesures || []) {
            const key = this.mesureKey(mesure);

            if (seen[key]) continue;

            seen[key] = true;
            out.push({
              key,
              id: mesure.id,
              name: mesure.name || mesure.csv_column || `#${mesure.id}`,
              csv_column: mesure.csv_column || "",
            });
          }
        }
      }

      return out;
    },

    unmappedCount() {
      return this.sourceMesures.filter((m) => !this.mapping[m.key]).length;
    },
  },

  watch: {
    open(v) {
      if (v) {
        this.step = 1;
        this.sourceId = null;
        this.sourceVisualizations = [];
        this.pickedIds = [];
        this.mapping = {};
        this.autoMatched = 0;
      }
    },
  },

  methods: {
    mesureKey(mesure) {
      return String(mesure.id) + "|" + (mesure.csv_column || "");
    },

    axesOf(viz) {
      return (viz.axes && viz.axes.axes) || [];
    },

    axeCount(viz) {
      return this.axesOf(viz).length;
    },

    mesureCount(viz) {
      return this.axesOf(viz).reduce((n, a) => n + ((a.mesures || []).length), 0);
    },

    togglePick(viz) {
      const i = this.pickedIds.indexOf(viz.id);

      if (i >= 0) {
        this.pickedIds.splice(i, 1);
      } else {
        this.pickedIds.push(viz.id);
      }
    },

    async onSourceChange() {
      this.sourceVisualizations = [];
      this.pickedIds = [];

      if (!this.sourceId) return;

      this.loadingViz = true;

      try {
        const result = await this.$store.dispatch("visualizationsRequest", {
          site_id: this.sourceId,
        });

        this.sourceVisualizations = (result && result.data) ? result.data : (result || []);
      } catch (err) {
        this.showErr(err);
      } finally {
        this.loadingViz = false;
      }
    },

    /**
     * Pré-remplit la correspondance sur la colonne source, qui est le seul
     * identifiant stable d'un univers à l'autre. Le nom sert de repli.
     */
    goToMapping() {
      const mapping = {};
      let auto = 0;

      for (const m of this.sourceMesures) {
        let match = null;

        if (m.csv_column) {
          match = this.targetSensors.find(
              (s) => (s.csv_column || "").trim() === m.csv_column.trim()
          );
        }

        if (!match && m.name) {
          match = this.targetSensors.find(
              (s) => (s.name || "").trim() === m.name.trim()
          );
        }

        if (match) {
          mapping[m.key] = match.id;
          auto++;
        } else {
          mapping[m.key] = null;
        }
      }

      this.mapping = mapping;
      this.autoMatched = auto;
      this.step = 3;
    },

    /**
     * Reconstruit un graphique pour l'univers de destination.
     * Chaque mesure est remplacée par l'objet capteur cible complet : le
     * composant de rendu lit id, site_id et csv_column dessus.
     */
    remap(viz) {
      const axes = [];

      for (const axe of this.axesOf(viz)) {
        const mesures = [];

        for (const mesure of axe.mesures || []) {
          const targetId = this.mapping[this.mesureKey(mesure)];

          if (!targetId) continue;

          const target = this.targetSensors.find((s) => s.id === targetId);

          if (!target) continue;

          // On garde les réglages d'affichage propres à la mesure dans ce
          // graphique (couleur, style...), on ne remplace que l'identité.
          mesures.push({
            ...mesure,
            ...target,
          });
        }

        // Un axe vidé de ses mesures n'a plus de raison d'être.
        if (!mesures.length) continue;

        axes.push({...axe, mesures});
      }

      return {
        name: viz.name,
        component: viz.component || null,
        site_id: this.site.id,
        axes: {...(viz.axes || {}), axes},
        // Les évènements sont propres à l'univers source : on ne les reprend pas.
        events: [],
        annotations: viz.annotations || null,
      };
    },

    async confirm() {
      this.working = true;

      let created = 0;
      let skipped = 0;

      try {
        for (const viz of this.pickedVisualizations) {
          const payload = this.remap(viz);

          // Un graphique dont plus aucune mesure ne correspond serait vide.
          if (!payload.axes.axes.length) {
            skipped++;
            continue;
          }

          await this.$store.dispatch("visualizationCreateRequest", payload);
          created++;
        }

        let msg = `${created} graphique(s) dupliqué(s).`;

        if (skipped) {
          msg += ` ${skipped} ignoré(s), faute de mesure correspondante.`;
        }

        this.$store.commit("setSnack", {type: "green", err: msg});

        this.$emit("imported");
        this.$emit("close");

      } catch (err) {
        this.showErr(err);
      } finally {
        this.working = false;
      }
    },

    showErr(err) {
      const data = err && err.response && err.response.data;
      const message = (data && data.message) || data || "Une erreur est survenue";

      this.$store.commit("setSnack", {
        type: "red",
        err: typeof message === "string" ? message : JSON.stringify(message),
      });
    },
  },
};
</script>

<style scoped>
.vi-card {
  border-radius: 12px;
}

.vi-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2d24;
}

.vi-step {
  font-size: 0.74rem;
  color: #8a9a90;
  font-weight: 400;
}

.vi-body {
  padding-top: 18px !important;
  max-height: 60vh;
}

.vi-intro {
  font-size: 0.82rem;
  color: #8a9a90;
  margin: 0 0 16px;
}

.vi-empty,
.vi-loading {
  font-size: 0.85rem;
  color: #8a9a90;
  font-style: italic;
  padding: 14px 0;
}

.vi-list {
  border: 1px solid #e6ebe8;
  border-radius: 10px;
  overflow: hidden;
}

.vi-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid #f0f3f1;
  cursor: pointer;
  margin: 0;
  transition: background 0.15s;
}

.vi-item:last-child {
  border-bottom: none;
}

.vi-item:hover {
  background: #f6f8f7;
}

.vi-item.selected {
  background: #eef5f0;
}

.vi-item-body {
  display: flex;
  flex-direction: column;
}

.vi-item-name {
  font-size: 0.88rem;
  font-weight: 500;
  color: #1f2d24;
}

.vi-item-meta {
  font-size: 0.72rem;
  color: #8a9a90;
}

.vi-map-head {
  display: grid;
  grid-template-columns: 1fr 32px 1fr;
  gap: 10px;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a9a90;
  font-weight: 600;
  padding-bottom: 6px;
  border-bottom: 1px solid #e6ebe8;
  margin-bottom: 8px;
}

.vi-map-row {
  display: grid;
  grid-template-columns: 1fr 32px 1fr;
  gap: 10px;
  align-items: center;
  padding: 7px 0;
  border-bottom: 1px solid #f4f6f5;
}

.vi-map-row.unmapped .vi-map-source {
  opacity: 0.5;
}

.vi-map-source {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.vi-map-name {
  font-size: 0.85rem;
  color: #1f2d24;
  font-weight: 500;
}

.vi-map-col {
  font-family: monospace;
  font-size: 0.72rem;
  color: #8a9a90;
}

.vi-map-arrow {
  text-align: center;
  color: #b0bdb5;
}

.vi-warn {
  margin-top: 16px;
  font-size: 0.78rem;
}

.vi-actions {
  padding: 12px 16px;
}
</style>
